import type { Site } from "./sites";
import type { User } from "./user";

export enum EmployeeCountRange {
  R_1_10 = "1-10",
  R_10_50 = "10-50",
  R_50_100 = "50-100",
  R_100_200 = "100-200",
  R_200_500 = "200-500",
  R_500_1000 = "500-1000",
  R_1000_2000 = "1000-2000",
  R_2000_5000 = "2000-5000",
  R_5000_PLUS = "5000+",
}

export enum RevenueRange {
  UNDER_100K = "<100k",
  R_100K_500K = "100k-500k",
  R_500K_2M = "500k-2M",
  R_2M_15M = "2M-15M",
  R_15M_50M = "15M-50M",
  R_50M_100M = "50M-100M",
  R_100M_500M = "100M-500M",
  OVER_500M = "500M+",
}

export enum CompanyType {
  PRIVATE = "private",
  PUBLIC = "public",
}

export enum Industry {
  AGRICULTURE = "agriculture",
  APPAREL = "apparel",
  BANKING = "banking",
  BIOTECHNOLOGY = "biotechnology",
  CHEMICALS = "chemicals",
  COMMUNICATIONS = "communications",
  CONSTRUCTION = "construction",
  CONSULTING = "consulting",
  EDUCATION = "education",
  ELECTRONICS = "electronics",
  ENERGY = "energy",
  ENGINEERING = "engineering",
  ENTERTAINMENT = "entertainment",
  ENVIRONMENTAL = "environmental",
  FINANCE = "finance",
  FOOD_AND_BEVERAGE = "food_and_beverage",
  GOVERNMENT = "government",
  HEALTHCARE = "healthcare",
  HOSPITALITY = "hospitality",
  INSURANCE = "insurance",
  MACHINERY = "machinery",
  MANUFACTURING = "manufacturing",
  MEDIA = "media",
  NOT_FOR_PROFIT = "not_for_profit",
  RECREATION = "recreation",
  RETAIL = "retail",
  SHIPPING = "shipping",
  TECHNOLOGY = "technology",
  TELECOMMUNICATIONS = "telecommunications",
  TRANSPORTATION = "transportation",
  UTILITIES = "utilities",
  OTHER = "other",
}

export const INDUSTRY_LABELS: Record<Industry, string> = {
  [Industry.AGRICULTURE]: "Agriculture",
  [Industry.APPAREL]: "Apparel",
  [Industry.BANKING]: "Banking",
  [Industry.BIOTECHNOLOGY]: "Biotechnology",
  [Industry.CHEMICALS]: "Chemicals",
  [Industry.COMMUNICATIONS]: "Communications",
  [Industry.CONSTRUCTION]: "Construction",
  [Industry.CONSULTING]: "Consulting",
  [Industry.EDUCATION]: "Education",
  [Industry.ELECTRONICS]: "Electronics",
  [Industry.ENERGY]: "Energy",
  [Industry.ENGINEERING]: "Engineering",
  [Industry.ENTERTAINMENT]: "Entertainment",
  [Industry.ENVIRONMENTAL]: "Environmental",
  [Industry.FINANCE]: "Finance",
  [Industry.FOOD_AND_BEVERAGE]: "Food & Beverage",
  [Industry.GOVERNMENT]: "Government",
  [Industry.HEALTHCARE]: "Healthcare",
  [Industry.HOSPITALITY]: "Hospitality",
  [Industry.INSURANCE]: "Insurance",
  [Industry.MACHINERY]: "Machinery",
  [Industry.MANUFACTURING]: "Manufacturing",
  [Industry.MEDIA]: "Media",
  [Industry.NOT_FOR_PROFIT]: "Not for Profit",
  [Industry.RECREATION]: "Recreation",
  [Industry.RETAIL]: "Retail",
  [Industry.SHIPPING]: "Shipping",
  [Industry.TECHNOLOGY]: "Technology",
  [Industry.TELECOMMUNICATIONS]: "Telecommunications",
  [Industry.TRANSPORTATION]: "Transportation",
  [Industry.UTILITIES]: "Utilities",
  [Industry.OTHER]: "Other",
};

export const INDUSTRY_OPTIONS = Object.values(Industry).map((value) => ({
  value,
  label: INDUSTRY_LABELS[value],
}));

export enum AlertCondition {
  MORE_THAN = "more_than",
  LESS_THAN = "less_than",
}

export enum AlertNotificationMode {
  EVERY_SCAN = "every_scan",
  ON_STATE_CHANGE = "on_state_change",
}

export interface SubClientCategoryAlert {
  id?: number;
  productId: number;
  productName?: string;
  condition: AlertCondition;
  threshold: number;
  notificationMode: AlertNotificationMode;
}

export interface ProductRotation {
  id?: number;
  productId: number;
  productName?: string;
  days: number;
  count: number;
}

export interface SubClient {
  id: number;
  name: string;
  /** Public URL of the uploaded logo; null when none. */
  logo?: string | null;
  address: string;
  latitude?: number;
  longitude?: number;
  contactPersonName?: string;
  contactPersonEmail?: string;
  contactPersonPhone?: string;
  industry?: Industry | null;
  site?: Site | null;
  employeeCount?: EmployeeCountRange;
  annualRevenue?: RevenueRange;
  companyType?: CompanyType;
  customFields?: Record<string, unknown> | null;
  alerts?: ServerSubClientAlert[];
  alertsCount?: number;
  rotations?: ServerSubClientRotation[];
  created_by?: User;
  updated_by?: User;
  createdAt: string;
  updatedAt: string;
}

export interface ServerSubClientAlert {
  id: number;
  condition: AlertCondition;
  threshold: number;
  notificationMode: AlertNotificationMode;
  product?: { id: number; name?: string };
}

export interface ServerSubClientRotation {
  id: number;
  days: number;
  count: number;
  product?: { id: number; name?: string };
}

export interface CreateSubClientPayload {
  name: string;
  /** Upload URL; `null` removes the logo. */
  logo?: string | null;
  address?: string;
  latitude?: number;
  longitude?: number;
  contactPersonName?: string | null;
  contactPersonEmail?: string | null;
  contactPersonPhone?: string | null;
  industry?: Industry | null;
  siteId?: number | null;
  employeeCount?: EmployeeCountRange | null;
  annualRevenue?: RevenueRange | null;
  companyType?: CompanyType | null;
  customFields?: Record<string, unknown> | null;
  alerts?: SubClientCategoryAlert[];
  rotations?: ProductRotation[];
}

export interface SubClientCategoryCount {
  productId: number;
  productName: string;
  count: number;
}

export interface SubClientAnalytics {
  categories: SubClientCategoryCount[];
  totalItems: number;
}
